import { gql } from '@apollo/client';

// Fragment for execution data
export const executionFragment = gql`
  fragment AnalyticsExecutionData on AnalyticsExecution {
    id
    orgId
    reportType
    startedAt
    completedAt
    status
    durationMs
    recipientCount
    rowCount
    pdfSizeBytes
    errorMessage
    triggeredBy
    createdAt
  }
`;

// Fragment for schedule view data
export const scheduleViewFragment = gql`
  fragment AnalyticsScheduleViewData on AnalyticsScheduleView {
    orgId
    orgName
    orgSlug
    timezone
    weeklyEnabled
    weeklyRecipientCount
    weeklyLastExecution {
      ...AnalyticsExecutionData
    }
    monthlyEnabled
    monthlyRecipientCount
    monthlyLastExecution {
      ...AnalyticsExecutionData
    }
    dailyCsvEnabled
    dailyCsvBucket
    dailyCsvLastExecution {
      ...AnalyticsExecutionData
    }
  }
  ${executionFragment}
`;

// Query: List all analytics schedules
export const analyticsSchedulesQuery = gql`
  query analyticsSchedules($organizationId: Long) {
    analyticsLambdaEnabled
    analyticsSchedules(organizationId: $organizationId) {
      ...AnalyticsScheduleViewData
    }
  }
  ${scheduleViewFragment}
`;

// Query: List executions with filtering and pagination
export const analyticsExecutionsQuery = gql`
  query analyticsExecutions(
    $organizationId: Long
    $reportType: String
    $status: String
    $daysBack: Int
    $limit: Int
    $offset: Int
  ) {
    analyticsExecutions(
      organizationId: $organizationId
      reportType: $reportType
      status: $status
      daysBack: $daysBack
      limit: $limit
      offset: $offset
    ) {
      items {
        ...AnalyticsExecutionData
      }
      totalCount
      limit
      offset
    }
  }
  ${executionFragment}
`;

// Query: Get latest execution for org/type
export const analyticsLatestExecutionQuery = gql`
  query analyticsLatestExecution($organizationId: Long!, $reportType: String!) {
    analyticsLatestExecution(organizationId: $organizationId, reportType: $reportType) {
      ...AnalyticsExecutionData
    }
  }
  ${executionFragment}
`;

// Query: Get recipient emails for org/report type (does not require Lambda)
export const analyticsScheduleRecipientsQuery = gql`
  query analyticsScheduleRecipients($organizationId: Long!, $reportType: String!) {
    analyticsScheduleRecipients(organizationId: $organizationId, reportType: $reportType)
  }
`;

// Mutation: Pause schedule
export const pauseAnalyticsScheduleMutation = gql`
  mutation pauseAnalyticsSchedule($organizationId: Long!, $reportType: String!) {
    pauseAnalyticsSchedule(organizationId: $organizationId, reportType: $reportType)
  }
`;

// Mutation: Resume schedule
export const resumeAnalyticsScheduleMutation = gql`
  mutation resumeAnalyticsSchedule($organizationId: Long!, $reportType: String!) {
    resumeAnalyticsSchedule(organizationId: $organizationId, reportType: $reportType)
  }
`;

// Mutation: Trigger report
export const triggerAnalyticsReportMutation = gql`
  mutation triggerAnalyticsReport($organizationId: Long!, $reportType: String!) {
    triggerAnalyticsReport(organizationId: $organizationId, reportType: $reportType) {
      success
      executionId
      message
    }
  }
`;

// Mutation: Retry failed execution
export const retryFailedExecutionMutation = gql`
  mutation retryFailedExecution($executionId: Long!) {
    retryFailedExecution(executionId: $executionId) {
      success
      executionId
      message
    }
  }
`;

// Mutation: Sync schedules (create tables + default schedules)
export const syncAnalyticsSchedulesMutation = gql`
  mutation syncAnalyticsSchedules {
    syncAnalyticsSchedules {
      success
      schedulesCreated
      schedulesUpdated
      message
    }
  }
`;

// Mutation: Preview report (returns full debug info)
export const previewAnalyticsReportMutation = gql`
  mutation previewAnalyticsReport($organizationId: Long!, $reportType: String!) {
    previewAnalyticsReport(organizationId: $organizationId, reportType: $reportType) {
      success
      pdfBase64
      filename
      sizeBytes
      message
      emailSubject
      emailFrom
      emailRecipients
      htmlContent
      dateRangeStart
      dateRangeEnd
      dateRangeDisplay
      csvContent
      csvRowCount
    }
  }
`;

export default {
  analyticsSchedulesQuery,
  analyticsExecutionsQuery,
  analyticsLatestExecutionQuery,
  analyticsScheduleRecipientsQuery,
  pauseAnalyticsScheduleMutation,
  resumeAnalyticsScheduleMutation,
  triggerAnalyticsReportMutation,
  retryFailedExecutionMutation,
  syncAnalyticsSchedulesMutation,
  previewAnalyticsReportMutation,
};
